import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Endpoints } from "../lib/api";
import { dayTimeOptions, shiftHours } from "../lib/time";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

export default function AgentsPage() {
  const { config, agents, professions, skeletons, refreshLists } = useApp();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const profName = Object.fromEntries(professions.map((p) => [p.id, p.name]));
  const days = config?.days || [];
  const timeOptions = dayTimeOptions(config);

  const skeletonByAgent = useMemo(() => {
    const m = {};
    skeletons.forEach((s) => {
      (m[s.agent_id] = m[s.agent_id] || {})[s.day] = { start: s.planned_start_time, end: s.planned_end_time };
    });
    return m;
  }, [skeletons]);

  const profById = useMemo(() => Object.fromEntries(professions.map((p) => [p.id, p])), [professions]);

  // Fail-fast HR validation: flag rosters that can never satisfy the profession's
  // legal limits or a contract window, before a solve is even attempted.
  const hrIssues = useMemo(() => {
    const labelOf = (date) => days.find((d) => d.date === date)?.label || date;
    const round = (n) => Math.round(n * 100) / 100;
    const out = [];
    agents.forEach((a) => {
      const p = profById[a.profession_id];
      const sk = skeletonByAgent[a.id] || {};
      if (!p) { out.push({ level: "error", agent: a.name, msg: "has no valid profession assigned." }); return; }
      const within = (date) =>
        (!a.contract_start_date || date >= a.contract_start_date) && (!a.contract_end_date || date <= a.contract_end_date);
      let weekly = 0, workingWithin = 0;
      Object.entries(sk).forEach(([date, e]) => {
        const h = shiftHours(e.start, e.end);
        if (h > p.daily_hour_limit + 1e-9)
          out.push({ level: "warn", agent: a.name, msg: `${labelOf(date)} shift is ${round(h)}h — over the ${p.name} daily cap of ${p.daily_hour_limit}h.` });
        if (within(date)) { weekly += h; workingWithin++; }
      });
      if (weekly > p.weekly_hour_limit + 1e-9)
        out.push({ level: "error", agent: a.name, msg: `weekly skeleton totals ${round(weekly)}h — over the ${p.name} weekly cap of ${p.weekly_hour_limit}h. Impossible to satisfy.` });
      if ((a.contract_start_date || a.contract_end_date) && workingWithin === 0)
        out.push({ level: "warn", agent: a.name, msg: "has a contract window but no shifts fall inside it — they will never be scheduled." });
    });
    return out;
  }, [agents, profById, skeletonByAgent, days]);

  const blankSkeleton = () =>
    Object.fromEntries(days.map((d) => [d.date, { on: false, start: "08:00", end: "16:00" }]));

  const [form, setForm] = useState({
    name: "", profession_id: "", avatar_url: null,
    contract_start_date: "", contract_end_date: "", skeleton: {},
  });

  const openCreate = () => {
    setEditId(null);
    setForm({
      name: "", profession_id: professions[0]?.id || "", avatar_url: null,
      contract_start_date: "", contract_end_date: "", skeleton: blankSkeleton(),
    });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditId(a.id);
    const sk = skeletonByAgent[a.id] || {};
    const skeleton = Object.fromEntries(days.map((d) => {
      const e = sk[d.date];
      return [d.date, e ? { on: true, start: e.start, end: e.end } : { on: false, start: "08:00", end: "16:00" }];
    }));
    setForm({
      name: a.name, profession_id: a.profession_id, avatar_url: a.avatar_url || null,
      contract_start_date: a.contract_start_date || "", contract_end_date: a.contract_end_date || "",
      skeleton,
    });
    setOpen(true);
  };

  const toggleDay = (date) =>
    setForm((f) => ({ ...f, skeleton: { ...f.skeleton, [date]: { ...f.skeleton[date], on: !f.skeleton[date]?.on } } }));
  const setDay = (date, patch) =>
    setForm((f) => ({ ...f, skeleton: { ...f.skeleton, [date]: { ...f.skeleton[date], ...patch } } }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.profession_id) return toast.error("Pick a profession");
    setBusy(true);
    try {
      const agentBody = {
        name: form.name.trim(),
        profession_id: form.profession_id,
        avatar_url: form.avatar_url || null,
        contract_start_date: form.contract_start_date || null,
        contract_end_date: form.contract_end_date || null,
      };
      let id = editId;
      if (editId) await Endpoints.updateAgent(editId, agentBody);
      else id = (await Endpoints.createAgent(agentBody)).id;

      const entries = days
        .filter((d) => form.skeleton[d.date]?.on)
        .map((d) => ({
          day: d.date,
          planned_start_time: form.skeleton[d.date].start,
          planned_end_time: form.skeleton[d.date].end,
        }));
      await Endpoints.setAgentSkeleton(id, entries);

      await refreshLists();
      setOpen(false);
      toast.success(editId ? "Agent updated" : "Agent added", { description: "Regenerate the schedule to apply." });
    } catch (e) {
      toast.error("Could not save agent", { description: e?.response?.data?.detail?.toString() || e.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await Endpoints.deleteAgent(id);
    await refreshLists();
    toast.success("Agent removed");
  };

  const contractLabel = (a) => {
    if (!a.contract_start_date && !a.contract_end_date) return "Permanent";
    return `${a.contract_start_date || "…"} → ${a.contract_end_date || "…"}`;
  };
  const workingLabels = (a) => {
    const sk = skeletonByAgent[a.id] || {};
    return days.filter((d) => sk[d.date]).map((d) => d.label);
  };

  return (
    <div className="animate-fadeup">
      <PageHeader title="Agents" subtitle="Employees, their profession (which carries the labour-law limits), optional fixed-term contract window, and their weekly shift skeleton.">
        <Button variant="primary" size="sm" onClick={openCreate} data-testid="add-agent-btn">
          <Plus className="w-4 h-4" /> New agent
        </Button>
      </PageHeader>

      <div className="mb-5" data-testid="hr-validation-panel">
        {hrIssues.length === 0 ? (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-sm border border-success/30 bg-success/10 text-sm">
            <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
            <span className="text-ink">All agent rosters are valid against their profession limits and contracts.</span>
          </div>
        ) : (
          <div className="rounded-sm border border-warning/40 bg-warning/10 overflow-hidden">
            <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-warning/20">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
              <span className="text-sm font-medium text-ink">{hrIssues.length} roster issue{hrIssues.length > 1 ? "s" : ""} to review before solving</span>
            </div>
            <ul className="divide-y divide-white/5">
              {hrIssues.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2 px-4 py-2 text-xs" data-testid={`hr-issue-${i.level}`}>
                  <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${i.level === "error" ? "bg-critical" : "bg-warning"}`} />
                  <span><span className="text-ink font-medium">{i.agent}</span> <span className="text-muted">{i.msg}</span></span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-line text-[11px] uppercase tracking-wider text-muted">
              <Th>Agent</Th><Th>Profession</Th><Th>Contract</Th><Th>Skeleton</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.id} className="border-b border-line last:border-0 hover:bg-white/5 transition-colors duration-100" data-testid={`agent-row-${a.id}`}>
                <Td>
                  <div className="flex items-center gap-2.5">
                    {a.avatar_url && <img src={a.avatar_url} alt="" className="w-7 h-7 rounded-sm object-cover border border-line" />}
                    <span className="text-ink font-medium">{a.name}</span>
                  </div>
                </Td>
                <Td><Badge>{profName[a.profession_id] || a.profession_id}</Badge></Td>
                <Td className="mono text-muted text-xs">
                  {(a.contract_start_date || a.contract_end_date)
                    ? <span className="text-warning">{contractLabel(a)}</span>
                    : <span className="text-faint">Permanent</span>}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {workingLabels(a).length
                      ? workingLabels(a).map((l) => <span key={l} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-surface2 border border-line text-muted">{l}</span>)
                      : <span className="text-faint text-xs">No shifts</span>}
                  </div>
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-agent-${a.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="danger" size="icon" onClick={() => remove(a.id)} data-testid={`delete-agent-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit agent" : "New agent"} testid="agent-dialog"
        footer={<><Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={busy} data-testid="save-agent-btn">
            {busy ? "Saving…" : editId ? "Save changes" : "Add agent"}
          </Button></>}>
        <Input label="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name-input" placeholder="e.g. Jordan Meyer" />
        <Select label="Profession" value={form.profession_id} onChange={(v) => setForm({ ...form, profession_id: v })}
          options={professions.map((p) => ({ value: p.id, label: p.name }))} testid="agent-profession-select" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Contract start (optional)" type="date" value={form.contract_start_date} onChange={(e) => setForm({ ...form, contract_start_date: e.target.value })} data-testid="agent-contract-start-input" />
          <Input label="Contract end (optional)" type="date" value={form.contract_end_date} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} data-testid="agent-contract-end-input" />
        </div>

        <div>
          <span className="block text-[11px] uppercase tracking-wider text-muted mb-1.5">Weekly schedule skeleton</span>
          <div className="space-y-1.5" data-testid="skeleton-builder">
            {days.map((d) => {
              const row = form.skeleton[d.date] || { on: false, start: "08:00", end: "16:00" };
              const overnight = row.on && row.end <= row.start;
              return (
                <div key={d.date} className="flex items-center gap-2">
                  <button type="button" onClick={() => toggleDay(d.date)} data-testid={`skeleton-toggle-${d.label}`}
                    className={`w-14 h-8 rounded-sm border text-xs shrink-0 transition-colors duration-150 ${row.on ? "bg-primary/15 border-primary/40 text-ink" : "bg-surface2 border-line text-faint hover:border-lineFocus"}`}>
                    {d.label}
                  </button>
                  {row.on ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex-1 min-w-0"><Select value={row.start} onChange={(v) => setDay(d.date, { start: v })} options={timeOptions} testid={`skeleton-start-${d.label}`} /></div>
                      <span className="text-faint text-xs">–</span>
                      <div className="flex-1 min-w-0"><Select value={row.end} onChange={(v) => setDay(d.date, { end: v })} options={timeOptions} testid={`skeleton-end-${d.label}`} /></div>
                      {overnight && <span className="mono text-[10px] text-primary shrink-0">+1d</span>}
                    </div>
                  ) : <span className="text-xs text-faint flex-1">Rest day</span>}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted mt-1.5">Toggle a day on/off. An end time earlier than the start (e.g. 22:00–06:00) is an overnight shift.</p>
        </div>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
